import { device } from "shared/styles/helpers";
import styled, { css } from "styled-components";
import theme from "styled-theming";
import colors from "../styles/colors";
import { IndentedMarginTop, IndentedMarginTopSmall, NavBarHeight, PageHeaderContainerHeightWithMargin, ThemeType } from "../styles/theme";

export enum ListItemType {
    primary = "primary",
    warning = "warning"
}

export const listItemTextColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_4
});

export const listItemBackgroundColor = theme.variants("mode", "variant", {
    [ListItemType.primary]: {
        [ThemeType.light]: colors.transparent,
        [ThemeType.dark]: colors.dark2
    },
    [ListItemType.warning]: {
        [ThemeType.light]: colors.orangeBackground,
        [ThemeType.dark]: colors.darkOrangeBackground
    }
});

export const listItemBorderColor = theme("mode", {
    [ThemeType.light]: colors.grey_1,
    [ThemeType.dark]: colors.grey_1,
});

export const listItemBorderColorActive = theme("mode", {
    [ThemeType.light]: colors.purpleSecondary,
    [ThemeType.dark]: colors.grey_2,
});

export const listItemBackgroundColorActive = theme("mode", {
    [ThemeType.light]: colors.purpleBackground,
    [ThemeType.dark]: colors.dark5,
});

export const SidePanelLayout = styled.div`
`
export const SidePanelFlexContainer = styled.div`
    gap: 20px;
    display: flex;
    .api-access-container {
        display: flex;
        flex: auto;
        gap:20px;
    }

    .content-container {
        flex: 1;
        margin-left: 10px;
        
        .panel {
            flex-direction: column;
            display: flex;
            height: 100%;
            border-bottom: 0;
            
            .panel-body {
                overflow: auto;
                height: 100%;
                padding: 25px;

                .panel-padding {
                    padding: 10px;
                }
            }
        }
    }
`
const getTop = (pageHeaderContainerHeightWithMargin?: string) => {
    return `${NavBarHeight} + ${pageHeaderContainerHeightWithMargin || PageHeaderContainerHeightWithMargin}`;
}

const getSmallTop = (pageHeaderContainerHeightWithMargin?: string) => {
    return `${getTop(pageHeaderContainerHeightWithMargin)} + ${IndentedMarginTopSmall}`;
}

const getLargeTop = (pageHeaderContainerHeightWithMargin?: string) => {
    return `${getTop(pageHeaderContainerHeightWithMargin)} + ${IndentedMarginTop}`;
}

const getNavigationMenuSize = (navigationMenuSize?: string) => `${navigationMenuSize || '0px'}`;

interface SidePanelContainerProps {
    pageHeaderContainerHeightWithMargin?: string; 
    navigationMenuSize?: string;
}

export const SidePanelContainer = styled.div<SidePanelContainerProps>`
    flex-direction: column;
    display: flex;
    flex: 0 1 30%;
    min-width: 400px;
    gap: 20px;
    position: sticky;
    
    height: calc(100vh - 324px - ${props => getNavigationMenuSize(props.navigationMenuSize)}); // Remove navigation, footer and title
    top: calc(${props => `${getSmallTop(props.pageHeaderContainerHeightWithMargin)} + ${getNavigationMenuSize(props.navigationMenuSize)}`});
    @media ${device.laptop} {
        top: calc(${props => `${getLargeTop(props.pageHeaderContainerHeightWithMargin)} + ${getNavigationMenuSize(props.navigationMenuSize)}`});
    }
    align-self: flex-start;
    .virtualized-container { 
        overflow: hidden;
        .ReactVirtualized__List {
            overflow: overlay !important;
            overscroll-behavior: contain;
            /* Track */
            ::-webkit-scrollbar-track {
                margin-block: 20px;
            }
        }
        .ReactVirtualized__Grid__innerScrollContainer {
            border: 1px solid ${colors.grey_1};
            border-radius:25px;
        }
    }
`

export const ListContainer = styled.div`
    flex: 1;
    overflow: auto;

    .ReactVirtualized__Grid__innerScrollContainer {
        border: 0 !important;
        border-radius: 0 !important;
    }
`

export const NonVirtualizedListContainer = styled.div`
    border: 1px solid ${colors.grey_1};
    border-radius:25px;
    overscroll-behavior: contain;
    overflow: overlay;
    ::-webkit-scrollbar-track {
        margin-block: 20px;
    }
`

const StyledListItemActiveCss = css<IListItemProps>`
    border: 1px solid ${listItemBorderColorActive};

    ${props => !props.$isLast && css`
        border-bottom: 0;
    `}

    ${props => props.index !== 0 && !props.$isLast && css`
        border-bottom: 0;
    `}

    + div, + a {
        border-top: 1px solid ${listItemBorderColorActive};
    }
`;

// using box shadow prevents items from jumping around when borders change
export const ListItem = styled.div<IListItemProps>`
    ${props => props.style};

    overflow: hidden;
    margin-bottom: 0px;
    display: flex;
    cursor: pointer;
    word-break: break-word;
    white-space: nowrap;
    color: ${listItemTextColor};

    svg path {
        fill: ${listItemTextColor};
    }

    :active {
        color: unset;
    }

    background-color: ${listItemBackgroundColor};
    border: 1px solid ${listItemBorderColor};
    border-bottom: 0;
    box-shadow: none;

    /*First item*/
    ${props => props.index === 0 && css`
        border-radius: 25px 25px 0 0;
    `}

    /*Last item*/
    ${props => props.$isLast && css`
        border-bottom: 1px solid ${colors.grey_1};
        border-radius: 0 0 25px 25px;  
    `}

    /*Only item*/
    ${props => props.index === 0 && props.$isLast && css`
        border-bottom: 1px solid ${colors.grey_1};
        border-radius: 25px;  
    `}

    ${props => props.$isActive && css<IListItemProps>`
        ${StyledListItemActiveCss}

        background-color: ${listItemBackgroundColorActive};
    `}

    :hover {
        ${StyledListItemActiveCss};
    }

    ${props => props.$asColumn ? css`
        justify-content: center; 
        flex-direction: column;
        flex-wrap: nowrap;
        align-content: flex-start;
    ` : css`
        align-items: center;
        justify-content: space-between;
    `}
`

export const ListItemTitle = styled.h3`
    margin: 0 20px 0 20px;
    overflow: hidden;
    text-overflow: ellipsis;
`

export const MultiRowListItemSpan = styled.span`
   margin-left: 20px;
   margin-right: 20px;
   margin-top: 5px;
   text-overflow: ellipsis;
   overflow: hidden;
`

export interface IListItemProps {
    style?: any;
    index?: number;
    $asColumn?: boolean;
    $isActive?: boolean;
    $isLast?: boolean;
    variant?: ListItemType;
}


ListItem.defaultProps = {
    variant: ListItemType.primary
}